import React, { useMemo } from 'react';
import IconSystem from 'icon-collection/icon-systems';
import { formatCurrency } from '../booking.utils';

// ============================================================================
// TYPES
// ============================================================================

/** Form-level advance state. Held alongside additionalData on BookingFormModal. */
export interface AdvanceData {
  advanceDueDate: string;
  advanceRequired: number;
  /**
   * Mandatory remarks when the operator-supplied advance is looser than the
   * configured policy. Validated by the parent on save; the chip in this
   * section warns the cashier when a remarks entry is required.
   */
  advanceOverrideRemarks: string;
}

interface AdvanceSectionProps {
  data: AdvanceData;
  onChange: (updates: Partial<AdvanceData>) => void;
  fieldErrors: Record<string, string>;
  expanded: boolean;
  onToggle: () => void;
  /** Config-driven advance policy — null while loading or unavailable */
  advanceConfig?: { advancePercent: number; advanceDueDays: number } | null;
  /** Net (post-discount) total used as the advance base. */
  netAmount?: number;
  /** Disabled when the booking is complimentary (advance is irrelevant). */
  isComplimentary: boolean;
  /** True when source has is_credit_eligible AND no operator advance entered. */
  isSourceEligible?: boolean;
  /** Policy-computed advance amount, used in the loose-override chip. */
  policyAmount?: number;
  /** Policy-computed due date (yyyy-mm-dd), used in the loose-override chip. */
  policyDueDate?: string;
  /** True when operator entry is looser than policy — drives the chip + remarks requirement. */
  isOverrideLoose?: boolean;
}

// ============================================================================
// COMPONENT
// ============================================================================

const AdvanceSection: React.FC<AdvanceSectionProps> = ({
  data, onChange, fieldErrors, expanded, onToggle,
  advanceConfig, netAmount, isComplimentary, isSourceEligible,
  policyAmount, policyDueDate, isOverrideLoose,
}) => {
  // Show indicator next to the section title when an advance is set OR override fires.
  const hasData = data.advanceRequired > 0 || !!data.advanceDueDate || !!isOverrideLoose;

  const minAdvance = useMemo(() => {
    const base = netAmount && netAmount > 0 ? netAmount : 0;
    if (!advanceConfig || base <= 0) return 0;
    return base * (advanceConfig.advancePercent / 100);
  }, [advanceConfig, netAmount]);

  const advanceFieldsDisabled = isComplimentary;

  return (
    <div>
      <div
        className={`collapsible-section-header${expanded ? ' expanded' : ''}`}
        onClick={onToggle}
        role="button"
        tabIndex={0}
        onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); onToggle(); } }}
      >
        <IconSystem.chevronRight size={14} className="chevron" />
        <span style={{ fontSize: '0.8rem', fontWeight: 600, color: 'var(--text-primary)' }}>
          Advance Payment
        </span>
        {hasData && (
          <span style={{ width: '6px', height: '6px', borderRadius: '50%', background: 'var(--primary)', marginLeft: '0.25rem' }} />
        )}
        {isOverrideLoose && (
          <span
            style={{
              fontSize: '0.7rem',
              padding: '0.125rem 0.4rem',
              borderRadius: '0.25rem',
              background: '#fef3c7',
              color: '#92400e',
              fontWeight: 600,
              marginLeft: '0.5rem',
            }}
          >
            Override
          </span>
        )}
      </div>

      <div className={`collapsible-section-body${expanded ? ' expanded' : ''}`}>
        {advanceFieldsDisabled && (
          <div style={{
            fontSize: '0.75rem',
            padding: '0.5rem 0.75rem',
            marginTop: '0.5rem',
            marginBottom: '0.5rem',
            borderRadius: '0.25rem',
            background: 'var(--bg-secondary, #f8fafc)',
            color: 'var(--text-secondary)',
          }}>
            Complimentary booking — no advance required.
          </div>
        )}

        <div className="form-grid-2" style={{ marginTop: '0.5rem' }}>
          <div>
            <label className="form-label">Advance Due Date</label>
            <input
              type="date"
              className={`form-input${fieldErrors['advanceDueDate'] ? ' form-field-error' : ''}`}
              value={data.advanceDueDate}
              disabled={advanceFieldsDisabled}
              onChange={e => onChange({ advanceDueDate: e.target.value })}
            />
            {fieldErrors['advanceDueDate'] && <div className="form-error-message">{fieldErrors['advanceDueDate']}</div>}
            {advanceConfig && !advanceFieldsDisabled && (
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                Policy: {policyDueDate || `today + ${advanceConfig.advanceDueDays} days`}{isSourceEligible ? ' — optional for credit-eligible' : ''}
              </div>
            )}
          </div>
          <div>
            <label className="form-label">Advance Required</label>
            <input
              type="number"
              className={`form-input${fieldErrors['advanceRequired'] ? ' form-field-error' : ''}`}
              value={data.advanceRequired || ''}
              onChange={e => onChange({ advanceRequired: parseFloat(e.target.value) || 0 })}
              min={0}
              step={0.01}
              placeholder="0.00"
              disabled={advanceFieldsDisabled}
            />
            {fieldErrors['advanceRequired'] && <div className="form-error-message">{fieldErrors['advanceRequired']}</div>}
            {minAdvance > 0 && !advanceFieldsDisabled && (
              <div style={{ fontSize: '0.7rem', color: 'var(--text-muted)', marginTop: '0.125rem' }}>
                Policy: {formatCurrency(minAdvance)} ({advanceConfig?.advancePercent}% of net){isSourceEligible ? ' — optional for credit-eligible' : ''}
              </div>
            )}
          </div>
        </div>

        {/* Override-loosening chip + mandatory remarks. Mirrors backend
            ADVANCE_OVERRIDE rule (BookingService.ResolveAdvanceAndStatusAsync). */}
        {isOverrideLoose && !advanceFieldsDisabled && (
          <div
            style={{
              marginTop: '0.75rem',
              padding: '0.625rem 0.875rem',
              background: '#fef3c7',
              border: '1px solid #f59e0b',
              borderRadius: '6px',
              color: '#78350f',
              fontSize: '0.8125rem',
              display: 'flex',
              gap: '0.5rem',
              alignItems: 'flex-start',
            }}
          >
            <IconSystem.alert size={16} />
            <div>
              <strong>Override needs approval.</strong> The entered advance is looser than policy
              (policy: {policyAmount && policyAmount > 0 ? formatCurrency(policyAmount) : '—'} by {policyDueDate || '—'}).
              Booking will be saved as <strong>PENDING_APPROVAL</strong>; remarks below are mandatory.
            </div>
          </div>
        )}

        {isOverrideLoose && !advanceFieldsDisabled && (
          <div style={{ marginTop: '0.75rem' }} data-field="advanceOverrideRemarks">
            <label className="form-label">
              Override Remarks <span className="text-danger">*</span>
            </label>
            <textarea
              className={`form-textarea${fieldErrors['advanceOverrideRemarks'] ? ' form-field-error' : ''}`}
              value={data.advanceOverrideRemarks}
              onChange={e => onChange({ advanceOverrideRemarks: e.target.value })}
              rows={2}
              maxLength={500}
              placeholder="Why is this override being requested? (audited and shown to the approver)"
              style={{ resize: 'vertical', minHeight: '50px' }}
            />
            {fieldErrors['advanceOverrideRemarks'] && (
              <div className="form-error-message">{fieldErrors['advanceOverrideRemarks']}</div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default AdvanceSection;
